import * as React from 'react';

import type { NodeDOMAttributes } from '@gitbook/slate-react';

import { flex, spacing } from '../constants';
import { NonEditableView } from '../DragAndDrop';
import { Interactive } from '../Interactive';
import { themedFonts, useCurrentTheme } from '../theme';
import { normalizeStyle, StyleSheet } from '../web';
import { CODEBLOCK_LINE_GUTTER_WIDTH, CodeBlockLineNumber } from './CodeBlockLineNumber';
import { CodeBlockLayoutContext } from './contexts';
import { type CodeBlockLineHighlighting, highlightingThemedStyles } from './line-highlighting';

export interface CodeBlockLineProps {
    /** Inner text content of the line */
    children: React.ReactNode;
    /** Current line number */
    number: number;
    /** Highlighting of the line. */
    highlighting?: CodeBlockLineHighlighting;
    /** Callback when pressing the line's gutter */
    onPressGutter?: () => void;
    attributes?: NodeDOMAttributes;
}

/**
 * Render a plain text code block line.
 * The inner content can be editable or not.
 */
export function CodeBlockLine({
    ref,
    ...props
}: CodeBlockLineProps & { ref?: React.Ref<HTMLDivElement> }): React.ReactElement {
    const { children, number, onPressGutter, attributes } = props;
    let { highlighting } = props;

    const gutterInteractiveState = Interactive.useInteractiveState();

    // Highlight the line when the gutter is hovered
    if (gutterInteractiveState.hovered) {
        highlighting = 'highlighted';
    }

    const [, font] = useCurrentTheme(themedFonts);
    const [, highlightingStyles] = useCurrentTheme(
        // @ts-ignore
        highlighting ? highlightingThemedStyles[highlighting] : undefined
    );
    const { overflow, lineNumbers } = React.useContext(CodeBlockLayoutContext);

    return (
        <div {...attributes} ref={ref}>
            <div
                className="flex"
                style={normalizeStyle([styles.codeBlockLine, highlightingStyles?.line])}
            >
                {lineNumbers === false ? (
                    <NonEditableView>
                        {onPressGutter ? (
                            <button
                                type="button"
                                className="flex-1 appearance-none border-0 bg-transparent p-0"
                                style={normalizeStyle([
                                    flex.flex1,
                                    styles.codeBlockLineCollapsedGutter,
                                ])}
                                onClick={onPressGutter}
                                {...gutterInteractiveState.domEvents}
                            />
                        ) : (
                            <div
                                className="flex-1"
                                style={normalizeStyle([
                                    flex.flex1,
                                    styles.codeBlockLineCollapsedGutter,
                                ])}
                            />
                        )}
                    </NonEditableView>
                ) : (
                    <CodeBlockLineNumber
                        number={(typeof lineNumbers === 'number' ? lineNumbers - 1 : 0) + number}
                        highlighting={highlighting}
                        onPress={onPressGutter}
                        gutterInteractiveState={gutterInteractiveState}
                    />
                )}

                <span
                    className={
                        overflow === 'scroll' ? 'inline-block whitespace-pre' : 'inline-block'
                    }
                    style={normalizeStyle([
                        styles.codeLineContent,
                        overflow === 'scroll' && styles.codeLineContentScroll,
                        overflow === 'wrap' && styles.codeLineContentWrap,
                        font.Code,
                        font.text,
                        highlightingStyles?.text,
                    ])}
                >
                    {children}
                </span>
            </div>
        </div>
    );
}

const styles = StyleSheet.create({
    codeBlockLine: {
        display: 'flex',
        flexDirection: 'row',
    },
    codeLineContent: {
        // To ensure the cursor is visible in an empty line,
        // we need a min-width larger than the padding caused by the line number
        minWidth: CODEBLOCK_LINE_GUTTER_WIDTH + spacing.small,
        paddingRight: spacing.medium,
    },
    codeLineContentScroll: {
        // @ts-ignore
        whiteSpace: 'pre',
    },
    codeLineContentWrap: {},
    codeBlockLineCollapsedGutter: {
        width: spacing.medium,
    },
});
